import React from "react";
import { type StyleProp, type TextStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { getEntityIconConfig, EntityType } from "@/constants/entity-icons";

export interface IEntityIconProps {
  /** The entity type (e.g. "memory", "media", "contact", "group", "share") */
  type: EntityType | string;
  /** Icon size in pixels (defaults to 18) */
  size?: number;
  /** Icon color (defaults to inherited tint) */
  color?: string;
  /** Whether to render the outline variant (defaults to false) */
  outline?: boolean;
  /** Optional style override */
  style?: StyleProp<TextStyle>;
  /** Accessibility label override */
  accessibilityLabel?: string;
}

/**
 * Standardized Archival Heirloom Entity Icon component.
 * Renders verified, unified icons for Memories, Media, Contacts, Groups, and Shares.
 *
 * @param {IEntityIconProps} props - Component properties.
 * @returns {React.JSX.Element} The rendered EntityIcon.
 */
export function EntityIcon({
  type,
  size = 18,
  color,
  outline = false,
  style,
  accessibilityLabel,
}: IEntityIconProps): React.JSX.Element {
  const config = getEntityIconConfig(type);
  const iconName = outline ? config.ioniconOutline : config.ionicon;

  return (
    <Ionicons
      name={iconName}
      size={size}
      color={color}
      style={style}
      accessibilityLabel={accessibilityLabel || config.label}
    />
  );
}

export default EntityIcon;
